import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { SendIcon } from "lucide-react";
import { cn } from "../../../utils";
/** The round send button every chat composer ends with. */ export function AiChatSendButton({ canSend, onClick, label = "Send message", className }) {
    return /*#__PURE__*/ _jsx("button", {
        type: "button",
        onClick: onClick,
        disabled: !canSend,
        className: cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", "bg-success text-white", "transition-opacity", canSend ? "hover:opacity-90" : "cursor-not-allowed opacity-50", className),
        "aria-label": label,
        children: /*#__PURE__*/ _jsx(SendIcon, {
            className: "h-4 w-4 -translate-x-px"
        })
    });
}
const MAX_HEIGHT_PX = 160;
/**
 * The chat input field: a framed, rounded surface with the auto-growing
 * textarea on top and the action row (host's `leftSlot` buttons, then send)
 * beneath it. Enter sends, Shift+Enter breaks the line.
 *
 * The frame lives HERE so the chat surfaces that adopt this composer share
 * the same field; the drop zone and attachment chips still belong to the host.
 */ export function AiChatComposer({ value, onChange, onSend, canSend, disabled, placeholder, sendLabel, className, leftSlot, leftSlotExpanded, onPasteFiles, textareaRef: externalRef, onFocus, onBlur }) {
    const internalRef = React.useRef(null);
    const setRefs = React.useCallback((el)=>{
        internalRef.current = el;
        if (externalRef) {
            externalRef.current = el;
        }
    }, [
        externalRef
    ]);
    // Auto-resize the textarea to fit its content.
    React.useEffect(()=>{
        const el = internalRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
    }, [
        value
    ]);
    const handleKeyDown = (e)=>{
        if (e.key === "Enter" && !e.shiftKey && canSend) {
            e.preventDefault();
            onSend();
        }
    };
    const handlePaste = (e)=>{
        if (!onPasteFiles) return;
        const files = Array.from(e.clipboardData?.files ?? []);
        // Only a FILE-ONLY clipboard is taken over. Copied spreadsheet cells travel
        // as text AND a rendered image, and pasting them as a screenshot instead of
        // as their text is never what was meant.
        const types = Array.from(e.clipboardData?.types ?? []);
        if (files.length === 0 || types.includes("text/plain")) {
            return;
        }
        e.preventDefault();
        onPasteFiles(files);
    };
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "ai-chat-composer",
        className: cn("flex flex-col gap-1 rounded-2xl border border-input bg-background p-2", "transition-[color,box-shadow] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40", className),
        children: leftSlotExpanded ? // `min-h-16` equals the idle stack (textarea line + gap + action row),
        // so starting or stopping a recording does not change the field height.
        /*#__PURE__*/ _jsx("div", {
            className: "flex min-h-16 items-center",
            children: leftSlot
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("textarea", {
                    ref: setRefs,
                    value: value,
                    onChange: (e)=>onChange(e.target.value),
                    onKeyDown: handleKeyDown,
                    onPaste: handlePaste,
                    onFocus: onFocus,
                    onBlur: onBlur,
                    placeholder: placeholder,
                    "aria-label": placeholder,
                    disabled: disabled,
                    rows: 1,
                    className: cn("w-full resize-none bg-transparent px-2 py-1 text-sm outline-hidden", "placeholder:text-muted-foreground", "disabled:cursor-not-allowed disabled:opacity-50"),
                    style: {
                        maxHeight: `${MAX_HEIGHT_PX}px`
                    }
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-1",
                    children: [
                        leftSlot,
                        /*#__PURE__*/ _jsx(AiChatSendButton, {
                            canSend: canSend,
                            onClick: onSend,
                            label: sendLabel,
                            className: "ml-auto"
                        })
                    ]
                })
            ]
        })
    });
}

//# sourceMappingURL=ai-chat-composer.js.map